import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { firestoreService } from '../shared/services/firestoreService';
import { USE_FIREBASE } from '../shared/services/apiConfig';
import { userMessage } from '../shared/userFacingError';
import { SpinnerIcon } from '../restaurateur/components/icons/SpinnerIcon';
import {
    APPLICATION_LIMITS,
    E164_PHONE,
    formatPhone,
    formatSiret,
    validateRejectionReason,
    type RestaurateurApplication,
    type RestaurateurApplicationStatus,
} from '../shared/restaurateurApplication';
import type { User } from '../shared/types';

/**
 * Compteur des demandes en attente (menu, tableau de bord) : n'écoute que les
 * demandes `pending` (au plus 200), monté dès l'ouverture d'AdminApp.
 */
export function usePendingRestaurateurApplications(): { count: number; capped: boolean } {
    const [count, setCount] = useState(0);
    useEffect(() => {
        if (!USE_FIREBASE) return;
        return firestoreService.subscribeToPendingRestaurateurApplications(
            (applications) => setCount(applications.length),
            () => setCount(0),
        );
    }, []);
    return { count, capped: count >= 200 };
}

/** Historique des demandes, écouté seulement quand l'onglet « Demandes » est ouvert. */
function useRestaurateurApplicationsHistory() {
    const [attempt, setAttempt] = useState(0);
    const [state, setState] = useState<{ applications: RestaurateurApplication[]; loading: boolean; error: string | null }>(
        { applications: [], loading: USE_FIREBASE, error: null },
    );
    useEffect(() => {
        if (!USE_FIREBASE) return;
        setState((s) => ({ ...s, loading: true, error: null }));
        return firestoreService.subscribeToRestaurateurApplications(
            (applications) => setState({ applications, loading: false, error: null }),
            (error) => setState((s) => ({ ...s, loading: false, error })),
        );
    }, [attempt]);
    return { ...state, retry: () => setAttempt((n) => n + 1) };
}

type Filter = RestaurateurApplicationStatus | 'all';

const FILTERS: { value: Filter; label: string }[] = [
    { value: 'pending', label: 'En attente' },
    { value: 'approved', label: 'Validées' },
    { value: 'rejected', label: 'Refusées' },
    { value: 'all', label: 'Toutes' },
];

const STATUS_BADGE: Record<RestaurateurApplicationStatus, { label: string; className: string }> = {
    pending: { label: 'En attente', className: 'bg-orange-100 text-orange-800' },
    approved: { label: 'Validée', className: 'bg-green-100 text-green-800' },
    rejected: { label: 'Refusée', className: 'bg-red-100 text-red-800' },
};

const formatDateTime = (iso: string) =>
    new Date(iso).toLocaleString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

type DialogState =
    | { kind: 'approve' | 'reject' | 'delete'; application: RestaurateurApplication }
    | null;

const RestaurateurApplicationsPanel: React.FC<{ users: User[] }> = ({ users }) => {
    const { applications, loading, error, retry } = useRestaurateurApplicationsHistory();
    const [filter, setFilter] = useState<Filter>('pending');
    const [dialog, setDialog] = useState<DialogState>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const headingRef = useRef<HTMLHeadingElement>(null);
    const noticeRef = useRef<HTMLDivElement>(null);
    // Élément qui a ouvert la boîte de dialogue : le focus y revient à l'annulation.
    const openerRef = useRef<HTMLElement | null>(null);

    // Autres demandes portant le même SIRET (fraude ou doublon à vérifier).
    const sameSiret = useMemo(() => {
        const bySiret = new Map<string, RestaurateurApplication[]>();
        for (const a of applications) bySiret.set(a.siret, [...(bySiret.get(a.siret) ?? []), a]);
        return (a: RestaurateurApplication) => (bySiret.get(a.siret) ?? []).filter((other) => other.id !== a.id);
    }, [applications]);

    useEffect(() => {
        if (notice) noticeRef.current?.focus();
    }, [notice]);

    if (!USE_FIREBASE) {
        return (
            <div className="bg-white p-8 rounded-2xl border border-gray-200 shadow-sm text-gray-600">
                Les demandes d'accès restaurateur ne sont pas disponibles en mode démo (elles nécessitent Firebase).
            </div>
        );
    }

    const count = (status: Filter) => (status === 'all' ? applications.length : applications.filter((a) => a.status === status).length);
    const visible = filter === 'all' ? applications : applications.filter((a) => a.status === filter);
    const adminName = (uid?: string) => {
        const admin = users.find((u) => u.id === uid);
        return admin ? `${admin.firstName} ${admin.lastName}`.trim() || admin.email : 'un admin';
    };

    const openDialog = (kind: 'approve' | 'reject' | 'delete', application: RestaurateurApplication) => {
        openerRef.current = document.activeElement as HTMLElement | null;
        setNotice(null);
        setDialog({ kind, application });
    };
    const cancelDialog = () => {
        setDialog(null);
        const opener = openerRef.current;
        // Le bouton d'origine a pu disparaître (demande traitée entre-temps) : titre de la liste.
        requestAnimationFrame(() => (opener?.isConnected ? opener : headingRef.current)?.focus());
    };
    const finishDialog = (message: string) => {
        setDialog(null);
        setNotice(message); // le focus va sur le message de confirmation (effet ci-dessus)
    };

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex flex-wrap justify-between items-center gap-4 bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
                <div className="max-w-2xl">
                    <h2 ref={headingRef} tabIndex={-1} className="text-lg font-bold text-gray-800 focus:outline-none">
                        Demandes d'accès restaurateur
                    </h2>
                    <p className="text-sm text-gray-600">
                        {count('pending')} en attente. Le lien « Annuaire des entreprises » prouve que le SIRET existe,
                        pas que le demandeur dirige l'établissement : comparez le nom du dirigeant et l'adresse avec la
                        demande, et appelez le demandeur en cas de doute.
                    </p>
                </div>
                <div className="flex flex-wrap gap-2" role="group" aria-label="Filtrer les demandes">
                    {FILTERS.map((f) => (
                        <button
                            key={f.value}
                            type="button"
                            onClick={() => setFilter(f.value)}
                            aria-pressed={filter === f.value}
                            className={`px-4 py-2 rounded-xl text-sm font-bold border transition-colors ${filter === f.value
                                ? 'bg-gray-900 text-white border-gray-900'
                                : 'bg-white text-gray-700 border-gray-300 hover:border-gray-500'}`}
                        >
                            {f.label} ({count(f.value)})
                        </button>
                    ))}
                </div>
            </div>

            <div role="status" aria-live="polite">
                {notice && (
                    <div
                        ref={noticeRef}
                        tabIndex={-1}
                        className="bg-green-50 border border-green-200 text-green-800 rounded-xl p-4 text-sm font-medium flex justify-between gap-4 focus:outline-none focus:ring-2 focus:ring-green-300"
                    >
                        <span>{notice}</span>
                        <button type="button" onClick={() => { setNotice(null); headingRef.current?.focus(); }} className="font-bold text-green-700 hover:text-green-900" aria-label="Fermer le message">✕</button>
                    </div>
                )}
            </div>

            {error ? (
                <div role="alert" className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm font-medium flex flex-wrap items-center justify-between gap-4">
                    <span>{error}</span>
                    <button type="button" onClick={retry} className="px-4 py-2 bg-white border border-red-300 rounded-xl font-bold text-red-700 hover:bg-red-100">
                        Réessayer
                    </button>
                </div>
            ) : loading ? (
                <div className="flex justify-center py-12" role="status" aria-label="Chargement des demandes"><SpinnerIcon className="w-8 h-8" /></div>
            ) : visible.length === 0 ? (
                <div className="bg-white p-10 rounded-2xl border border-gray-200 text-center text-gray-600">
                    {filter === 'pending' ? 'Aucune demande en attente.' : 'Aucune demande.'}
                </div>
            ) : (
                <ul className="space-y-4">
                    {visible.map((a) => (
                        <li key={a.id}>
                            <ApplicationCard
                                application={a}
                                decidedByName={adminName(a.decidedBy)}
                                duplicates={sameSiret(a)}
                                onApprove={() => openDialog('approve', a)}
                                onReject={() => openDialog('reject', a)}
                                onDelete={() => openDialog('delete', a)}
                            />
                        </li>
                    ))}
                </ul>
            )}
            {applications.length >= 500 && (
                <p className="text-xs text-gray-600">Seules les 500 demandes les plus récentes sont affichées.</p>
            )}

            {dialog?.kind === 'approve' && (
                <ApproveDialog
                    application={dialog.application}
                    onCancel={cancelDialog}
                    onDone={() => finishDialog(`Accès accordé à ${dialog.application.firstName} ${dialog.application.lastName} : le restaurant « ${dialog.application.restaurantName} » est créé. Son tableau de bord s'ouvre automatiquement (immédiatement s'il est connecté, sinon à sa prochaine connexion).`)}
                />
            )}
            {dialog?.kind === 'reject' && (
                <RejectDialog
                    application={dialog.application}
                    onCancel={cancelDialog}
                    onDone={() => finishDialog(`Demande de ${dialog.application.firstName} ${dialog.application.lastName} refusée : le motif lui est affiché dans l'application.`)}
                />
            )}
            {dialog?.kind === 'delete' && (
                <DeleteDialog
                    application={dialog.application}
                    onCancel={cancelDialog}
                    onDone={() => finishDialog(`Demande de ${dialog.application.firstName} ${dialog.application.lastName} supprimée.`)}
                />
            )}
        </div>
    );
};

const ApplicationCard: React.FC<{
    application: RestaurateurApplication;
    decidedByName: string;
    duplicates: RestaurateurApplication[];
    onApprove: () => void;
    onReject: () => void;
    onDelete: () => void;
}> = ({ application: a, decidedByName, duplicates, onApprove, onReject, onDelete }) => {
    const titleId = `application-${a.id}-title`;
    const approveHintId = `application-${a.id}-approve-hint`;
    const badge = STATUS_BADGE[a.status];
    // Défense en profondeur : formats revérifiés avant de construire un lien.
    const siretOk = /^\d{14}$/.test(a.siret);
    const phoneOk = E164_PHONE.test(a.phone);
    return (
        <article aria-labelledby={titleId} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <div className="flex flex-wrap justify-between items-start gap-4">
                <div>
                    <h3 id={titleId} className="text-lg font-bold text-gray-900">{a.restaurantName}</h3>
                    <p className="text-sm text-gray-600">
                        {a.firstName} {a.lastName} · demande du {formatDateTime(a.createdAt)}
                    </p>
                </div>
                <span className={`px-3 py-1 text-xs font-bold rounded-full ${badge.className}`}>{badge.label}</span>
            </div>

            {duplicates.length > 0 && (
                <p className="mt-4 bg-orange-50 border border-orange-200 text-orange-800 rounded-xl p-3 text-sm">
                    <span aria-hidden="true">⚠ </span>
                    Même SIRET que {duplicates.length > 1 ? `${duplicates.length} autres demandes` : 'une autre demande'} :{' '}
                    {duplicates.map((d) => `« ${d.restaurantName} » (${d.firstName} ${d.lastName}, ${STATUS_BADGE[d.status].label.toLowerCase()})`).join(', ')}.
                </p>
            )}

            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 mt-5 text-sm">
                <div>
                    <dt className="text-xs font-bold text-gray-600 uppercase tracking-wider">E-mail</dt>
                    <dd className="flex flex-wrap items-center gap-2 text-gray-800">
                        <a href={`mailto:${encodeURIComponent(a.email)}`} className="hover:underline break-all">{a.email}</a>
                        {a.emailVerified ? (
                            <span className="px-2 py-0.5 rounded text-xs font-bold bg-green-50 text-green-800">✓ vérifié</span>
                        ) : (
                            <span className="px-2 py-0.5 rounded text-xs font-bold bg-orange-50 text-orange-800">non vérifié</span>
                        )}
                    </dd>
                </div>
                <div>
                    <dt className="text-xs font-bold text-gray-600 uppercase tracking-wider">Téléphone</dt>
                    <dd className="text-gray-800">
                        {phoneOk
                            ? <a href={`tel:${a.phone}`} className="hover:underline tabular-nums">{formatPhone(a.phone)}</a>
                            : <span className="tabular-nums">{a.phone}</span>}
                    </dd>
                </div>
                <div>
                    <dt className="text-xs font-bold text-gray-600 uppercase tracking-wider">Adresse</dt>
                    <dd className="text-gray-800">{a.address}</dd>
                </div>
                <div>
                    <dt className="text-xs font-bold text-gray-600 uppercase tracking-wider">SIRET</dt>
                    <dd className="flex flex-wrap items-center gap-2 text-gray-800">
                        <span className="tabular-nums">{formatSiret(a.siret)}</span>
                        {siretOk && (
                            <a
                                href={`https://annuaire-entreprises.data.gouv.fr/etablissement/${a.siret}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs font-bold text-blue-700 hover:underline"
                            >
                                Voir l'établissement sur l'Annuaire des entreprises<span className="sr-only"> (nouvel onglet)</span> ↗
                            </a>
                        )}
                    </dd>
                </div>
            </dl>

            {a.status === 'rejected' && (
                <div className="mt-5 bg-red-50 border border-red-100 rounded-xl p-4 text-sm">
                    <p className="font-bold text-red-800">Refusée{a.decidedAt ? ` le ${formatDateTime(a.decidedAt)}` : ''} par {decidedByName}</p>
                    <p className="text-red-800 mt-1 whitespace-pre-line">{a.rejectionReason}</p>
                    {a.purgeAt && <p className="text-red-800 mt-2 text-xs">Suppression automatique prévue le {formatDateTime(a.purgeAt)}.</p>}
                </div>
            )}
            {a.status === 'approved' && (
                <p className="mt-5 bg-green-50 border border-green-100 rounded-xl p-4 text-sm text-green-800">
                    Validée{a.decidedAt ? ` le ${formatDateTime(a.decidedAt)}` : ''} par {decidedByName} · restaurant <span className="font-mono">{a.restaurantId}</span>
                </p>
            )}

            <div className="flex flex-wrap justify-end items-center gap-3 mt-6 pt-4 border-t border-gray-100">
                <button type="button" onClick={onDelete} className="mr-auto px-3 py-2 text-sm font-bold text-gray-700 rounded-xl hover:bg-gray-100 transition-colors">
                    Supprimer la demande<span className="sr-only"> de {a.restaurantName}</span>
                </button>
                {a.status === 'pending' && (
                    <>
                        {!a.emailVerified && (
                            <p id={approveHintId} className="text-xs text-gray-600 w-full text-right">
                                Validation possible une fois l'e-mail confirmé par le demandeur.
                            </p>
                        )}
                        <button type="button" onClick={onReject} className="px-5 py-2.5 border border-red-300 text-red-700 rounded-xl text-sm font-bold hover:bg-red-50 transition-colors">
                            Refuser<span className="sr-only"> la demande de {a.restaurantName}</span>
                        </button>
                        <button
                            type="button"
                            onClick={onApprove}
                            disabled={!a.emailVerified}
                            aria-describedby={a.emailVerified ? undefined : approveHintId}
                            className="px-5 py-2.5 bg-green-700 text-white rounded-xl text-sm font-bold hover:bg-green-800 transition-colors shadow disabled:bg-gray-300 disabled:text-gray-700 disabled:shadow-none disabled:cursor-not-allowed"
                        >
                            Valider<span className="sr-only"> la demande de {a.restaurantName}</span>
                        </button>
                    </>
                )}
            </div>
        </article>
    );
};

/**
 * Boîte de dialogue accessible : focus initial dans la fenêtre, Tab et Maj+Tab
 * restent dedans, Échap ou clic à l'extérieur demandent la fermeture
 * (`onRequestClose`, qui peut demander confirmation). Le retour du focus est
 * géré par le panneau.
 */
const Dialog: React.FC<{ title: string; onRequestClose: () => void; busy: boolean; children: React.ReactNode }> = ({ title, onRequestClose, busy, children }) => {
    const titleId = useId();
    const panelRef = useRef<HTMLDivElement>(null);
    const requestClose = useRef(onRequestClose);
    requestClose.current = onRequestClose;

    useEffect(() => {
        const first = panelRef.current?.querySelector<HTMLElement>('textarea, button:not([disabled])');
        first?.focus();
    }, []);

    const focusables = (): HTMLElement[] => Array.from<HTMLElement>(panelRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ) ?? []);

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            e.stopPropagation();
            if (!busy) requestClose.current();
            return;
        }
        if (e.key !== 'Tab') return;
        const items = focusables();
        if (items.length === 0) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && (document.activeElement === first || !panelRef.current?.contains(document.activeElement))) {
            e.preventDefault();
            last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
        }
    };

    return (
        <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={busy ? undefined : () => requestClose.current()}
        >
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                onKeyDown={onKeyDown}
                className="bg-white rounded-2xl shadow-2xl p-8 max-w-lg w-full animate-fade-in-up"
                onClick={(e) => e.stopPropagation()}
            >
                <h3 id={titleId} className="text-lg font-bold text-gray-900 mb-4">{title}</h3>
                {children}
            </div>
        </div>
    );
};

const DialogButtons: React.FC<{
    busy: boolean;
    onCancel: () => void;
    confirmLabel: string;
    busyLabel: string;
    tone: 'green' | 'red' | 'gray';
    onConfirm?: () => void;
}> = ({ busy, onCancel, confirmLabel, busyLabel, tone, onConfirm }) => {
    const tones = {
        green: 'bg-green-700 hover:bg-green-800',
        red: 'bg-red-600 hover:bg-red-700',
        gray: 'bg-gray-900 hover:bg-gray-800',
    };
    return (
        <div className="flex gap-3 mt-6">
            <button type="button" onClick={onCancel} disabled={busy} className="flex-1 px-4 py-3 border border-gray-300 rounded-xl text-gray-700 font-bold hover:bg-gray-50 transition-colors disabled:opacity-50">Annuler</button>
            <button
                type={onConfirm ? 'button' : 'submit'}
                onClick={onConfirm}
                disabled={busy}
                className={`flex-1 px-4 py-3 text-white rounded-xl font-bold transition-colors shadow-lg flex items-center justify-center gap-2 disabled:opacity-70 ${tones[tone]}`}
            >
                {busy && <SpinnerIcon />} {busy ? busyLabel : confirmLabel}
            </button>
        </div>
    );
};

const ApproveDialog: React.FC<{ application: RestaurateurApplication; onCancel: () => void; onDone: () => void }> = ({ application: a, onCancel, onDone }) => {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const approve = async () => {
        setBusy(true);
        setError(null);
        try {
            await firestoreService.approveRestaurateurApplication(a.id);
            onDone();
        } catch (err) {
            setError(userMessage(err, "La demande n'a pas pu être validée. Réessayez dans quelques instants."));
            setBusy(false);
        }
    };
    return (
        <Dialog title="Valider la demande" onRequestClose={onCancel} busy={busy}>
            <div className="space-y-3 text-sm text-gray-700">
                <p>
                    Le restaurant <span className="font-bold">« {a.restaurantName} »</span> sera créé et visible dans
                    l'application, et <span className="font-bold">{a.firstName} {a.lastName}</span> ({a.email}) aura accès
                    au tableau de bord restaurateur.
                </p>
                <p>
                    Avant de valider, vérifiez sur l'Annuaire des entreprises que l'établissement est actif et que le nom
                    du dirigeant et l'adresse correspondent à la demande.
                </p>
                {error && <p role="alert" className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 font-medium">{error}</p>}
            </div>
            <DialogButtons busy={busy} onCancel={onCancel} onConfirm={approve} tone="green" confirmLabel="Valider et créer le restaurant" busyLabel="Validation…" />
        </Dialog>
    );
};

const RejectDialog: React.FC<{ application: RestaurateurApplication; onCancel: () => void; onDone: () => void }> = ({ application: a, onCancel, onDone }) => {
    const [reason, setReason] = useState('');
    const [fieldError, setFieldError] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const reasonRef = useRef<HTMLTextAreaElement>(null);
    const fieldId = useId();
    const max = APPLICATION_LIMITS.rejectionReason.max;

    // Échap / clic extérieur : ne pas perdre un motif déjà saisi sans confirmation.
    const requestClose = () => {
        if (reason.trim() && !window.confirm('Abandonner le motif saisi ?')) {
            reasonRef.current?.focus();
            return;
        }
        onCancel();
    };

    const reject = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        const check = validateRejectionReason(reason);
        if (check.ok === false) {
            setFieldError(check.error);
            reasonRef.current?.focus();
            return;
        }
        setBusy(true);
        try {
            await firestoreService.rejectRestaurateurApplication(a.id, check.value);
            onDone();
        } catch (err) {
            setError(userMessage(err, "Le refus n'a pas pu être enregistré. Réessayez dans quelques instants."));
            setBusy(false);
        }
    };

    return (
        <Dialog title={`Refuser la demande de ${a.restaurantName}`} onRequestClose={requestClose} busy={busy}>
            <form onSubmit={reject} noValidate className="space-y-4">
                <div>
                    <label htmlFor={fieldId} className="block text-sm font-bold text-gray-700 mb-1">
                        Motif du refus <span className="font-normal text-gray-600">(obligatoire, affiché au demandeur)</span>
                    </label>
                    <textarea
                        id={fieldId}
                        ref={reasonRef}
                        value={reason}
                        onChange={(e) => { setReason(e.target.value); setFieldError(null); }}
                        rows={4}
                        maxLength={max}
                        required
                        aria-invalid={fieldError ? true : undefined}
                        aria-describedby={`${fieldId}-message`}
                        placeholder="Ex. : le SIRET ne correspond pas à un restaurant en activité."
                        className={`w-full border rounded-xl p-3 text-sm ${fieldError ? 'border-red-400' : 'border-gray-400'}`}
                        disabled={busy}
                    />
                    <p id={`${fieldId}-message`} className={`text-xs mt-1 ${fieldError ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
                        {fieldError ?? `${reason.trim().length} / ${max} caractères`}
                    </p>
                </div>
                {error && <p role="alert" className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm font-medium">{error}</p>}
                <DialogButtons busy={busy} onCancel={requestClose} tone="red" confirmLabel="Refuser la demande" busyLabel="Envoi…" />
            </form>
        </Dialog>
    );
};

const DeleteDialog: React.FC<{ application: RestaurateurApplication; onCancel: () => void; onDone: () => void }> = ({ application: a, onCancel, onDone }) => {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const remove = async () => {
        setBusy(true);
        setError(null);
        try {
            await firestoreService.deleteRestaurateurApplication(a.id);
            onDone();
        } catch (err) {
            setError(userMessage(err, "La demande n'a pas pu être supprimée. Réessayez dans quelques instants."));
            setBusy(false);
        }
    };
    return (
        <Dialog title="Supprimer la demande" onRequestClose={onCancel} busy={busy}>
            <div className="space-y-3 text-sm text-gray-700">
                <p>
                    La demande de <span className="font-bold">{a.firstName} {a.lastName}</span> pour
                    « {a.restaurantName} » sera supprimée définitivement (données personnelles et SIRET).
                </p>
                <p>
                    {a.status === 'approved'
                        ? "Le restaurant créé et l'accès restaurateur ne sont pas supprimés."
                        : 'Le demandeur pourra déposer une nouvelle demande.'}
                </p>
                {error && <p role="alert" className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 font-medium">{error}</p>}
            </div>
            <DialogButtons busy={busy} onCancel={onCancel} onConfirm={remove} tone="red" confirmLabel="Supprimer" busyLabel="Suppression…" />
        </Dialog>
    );
};

export default RestaurateurApplicationsPanel;
